import { ArrowLeft, ChevronDown, ChevronsUpDown, ChevronUp, X } from "lucide-react";
import {
  cloneElement,
  type FormEvent,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { APIError } from "../api/client";
import { titleCase } from "../lib/format";

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string | undefined;
  actions?: ReactNode | undefined;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function EditorPage({
  title,
  description,
  backTo,
  backLabel,
  children,
}: {
  title: string;
  description?: string | undefined;
  backTo: string;
  backLabel: string;
  children: ReactNode;
}) {
  return (
    <section className="editor-page">
      <PageHeader
        title={title}
        description={description}
        actions={
          <Link className="button secondary" to={backTo}>
            <ArrowLeft size={17} aria-hidden="true" /> {backLabel}
          </Link>
        }
      />
      {children}
    </section>
  );
}

export function Card({
  children,
  className = "",
  as: Component = "section",
}: {
  children: ReactNode;
  className?: string | undefined;
  as?: "section" | "article" | "div" | undefined;
}) {
  return <Component className={`card ${className}`}>{children}</Component>;
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string | undefined;
  actions?: ReactNode | undefined;
}) {
  return (
    <div className="card-header">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {actions}
    </div>
  );
}

export function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
}) {
  return (
    <Card className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </Card>
  );
}

/**
 * A status as a colored pill.
 *
 * `value` is the stored status and decides the color. `label` is optional and decides the words.
 * They are separate because the two answers are not the same: the tone has to be derived from the
 * machine value, while the wording is a content decision. Title-casing the raw value is a decent
 * default but it cannot be the only option, because it turns `part_paid` into "Part Paid" and has
 * no way to say "Partly paid". docs/20 is explicit that internal data names must not be shown as
 * ordinary user labels, so a caller holding a curated label needs somewhere to put it.
 *
 * Typed `string | undefined` rather than plain optional on purpose: callers index a label map, and
 * under exactOptionalPropertyTypes a lookup that may miss cannot be passed to a merely optional
 * property.
 */
export function StatusPill({ value, label }: { value: string; label?: string | undefined }) {
  // The same scar as titleCase in lib/format. Two of the branches below call `.includes` on
  // `value` inside an Array.some, so an absent field did not render an empty pill: it threw, and
  // with no error boundary in the tree it unmounted the entire application. The prop stays required
  // and typed `string`; this only stops a broken server response taking the navigation down too.
  if (typeof value !== "string" || value === "") {
    return <span className="status status-neutral">{label ?? "Unknown"}</span>;
  }
  const tone = [
    "confirmed",
    "active",
    "paid",
    "completed",
    "enabled",
    "succeeded",
    "verified",
    "accepted",
    "fulfilled",
    "received",
    "returned",
    "ready",
  ].includes(value)
    ? "success"
    : [
          "cancelled",
          "failed",
          "overdue",
          "declined",
          "rejected",
          "expired",
          "lost",
          "suspended",
          "blocked",
        ].includes(value)
      ? "danger"
      : ["pending", "provisional", "open", "processing", "held", "balance_due"].some((item) =>
            value.includes(item),
          )
        ? "warning"
        : [
              "enquiry",
              "issued",
              "in_progress",
              "submitted",
              "under_review",
              "part_paid",
              "proposal_sent",
              "qualified",
            ].some((item) => value.includes(item))
          ? "info"
          : "neutral";
  return <span className={`status status-${tone}`}>{label ?? titleCase(value)}</span>;
}

export interface Column<T> {
  label: string;
  render(item: T): ReactNode;
  className?: string | undefined;
  // Supplying this makes the column sortable. It is deliberately separate from
  // render(): a cell can display "12 Mar 2026" or a status pill while ordering on
  // the underlying ISO timestamp or rank, which is the whole reason sorting on
  // rendered text goes wrong.
  sortValue?(item: T): string | number;
}

type SortDirection = "ascending" | "descending";

export interface SortState {
  label: string;
  direction: SortDirection;
}

export function DataTable<T>({
  columns,
  items,
  getKey,
  caption,
  defaultSort,
}: {
  columns: Column<T>[];
  items: T[];
  getKey(item: T): string;
  caption: string;
  defaultSort?: SortState | undefined;
}) {
  const [sort, setSort] = useState<SortState | undefined>(defaultSort);
  const sorted = useMemo(() => {
    const column = columns.find((entry) => entry.label === sort?.label);
    const read = column?.sortValue;
    if (!sort || !read) return items;
    const factor = sort.direction === "ascending" ? 1 : -1;
    return [...items].sort((left, right) => {
      const a = read(left);
      const b = read(right);
      if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
      return String(a).localeCompare(String(b), undefined, { numeric: true }) * factor;
    });
  }, [columns, items, sort]);

  function toggle(label: string) {
    setSort((current) =>
      current?.label === label && current.direction === "ascending"
        ? { label, direction: "descending" }
        : { label, direction: "ascending" },
    );
  }

  return (
    <div className="table-scroll">
      <table>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const direction = sort && sort.label === column.label ? sort.direction : undefined;
              const Indicator =
                direction === "ascending"
                  ? ChevronUp
                  : direction === "descending"
                    ? ChevronDown
                    : ChevronsUpDown;
              return (
                <th
                  key={column.label}
                  className={column.className}
                  scope="col"
                  aria-sort={column.sortValue ? (direction ?? "none") : undefined}
                >
                  {column.sortValue ? (
                    <button
                      className="table-sort"
                      type="button"
                      onClick={() => toggle(column.label)}
                    >
                      {column.label}
                      <Indicator size={14} aria-hidden="true" />
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => (
            <tr key={getKey(item)}>
              {columns.map((column) => (
                <td key={column.label} className={column.className}>
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function ErrorPanel({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "An unexpected error occurred.";
  const requestId = error instanceof APIError ? error.requestId : undefined;
  return (
    <div className="notice notice-danger" role="alert">
      <strong>Something went wrong</strong>
      <span>{message}</span>
      {requestId ? <small>Request {requestId}</small> : null}
    </div>
  );
}

export function Notice({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "success" | "warning" | "danger" | undefined;
  title?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={`notice notice-${tone}`} role={tone === "danger" ? "alert" : "status"}>
      {title ? <strong>{title}</strong> : null}
      <span>{children}</span>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  const controlId = useId();
  const hintId = useId();
  const errorId = useId();
  let control = children;
  let labelFor: string | undefined;
  if (isValidElement(children)) {
    const element = children as ReactElement<{
      id?: string | undefined;
      "aria-describedby"?: string | undefined;
      "aria-invalid"?: boolean | undefined;
    }>;
    labelFor = element.props.id ?? controlId;
    const describedBy =
      [element.props["aria-describedby"], hint ? hintId : undefined, error ? errorId : undefined]
        .filter(Boolean)
        .join(" ") || undefined;
    const invalid = error ? true : element.props["aria-invalid"];
    control = cloneElement(element, {
      id: labelFor,
      ...(describedBy ? { "aria-describedby": describedBy } : {}),
      ...(invalid !== undefined ? { "aria-invalid": invalid } : {}),
    });
  }
  return (
    <div className="field">
      <label htmlFor={labelFor}>{label}</label>
      {control}
      {hint ? <small id={hintId}>{hint}</small> : null}
      {error ? (
        <small id={errorId} className="field-error" aria-live="polite">
          {error}
        </small>
      ) : null}
    </div>
  );
}

const modalStack: string[] = [];
let modalBodyOverflow: string | null = null;

export function Modal({
  title,
  description,
  open,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  description?: string | undefined;
  open: boolean;
  onClose(): void;
  children: ReactNode;
  wide?: boolean | undefined;
}) {
  const layerId = useId();
  const titleId = useId();
  const descriptionId = useId();
  const backdropRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const backdrop = backdropRef.current;
    const lowerLayers = Array.from(
      document.querySelectorAll<HTMLElement>(".modal-backdrop"),
    ).filter((element) => element !== backdrop);
    const previousInert = lowerLayers.map((element) => element.inert);
    lowerLayers.forEach((element) => {
      element.inert = true;
    });
    modalStack.push(layerId);
    if (modalStack.length === 1) modalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const modal = modalRef.current;
    const isTopLayer = () => modalStack[modalStack.length - 1] === layerId;
    const focusable = () =>
      Array.from(
        modal?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.getAttribute("aria-hidden") !== "true");
    const initialControls = focusable();
    const initial =
      modal?.querySelector<HTMLElement>("[data-autofocus]") ??
      initialControls.find((element) => !element.closest("header")) ??
      initialControls[0];
    if (initial) initial.focus();
    else modal?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopLayer()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        modal?.focus();
        return;
      }
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const wasTopLayer = modalStack[modalStack.length - 1] === layerId;
      const index = modalStack.lastIndexOf(layerId);
      if (index >= 0) modalStack.splice(index, 1);
      lowerLayers.forEach((element, layerIndex) => {
        element.inert = previousInert[layerIndex] ?? false;
      });
      if (modalStack.length === 0) {
        document.body.style.overflow = modalBodyOverflow ?? "";
        modalBodyOverflow = null;
      }
      if (wasTopLayer) previouslyFocused?.focus();
    };
  }, [layerId, open]);
  if (!open) return null;
  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: Backdrop pointer dismissal supplements Escape and the labeled close button.
    <div
      ref={backdropRef}
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && modalStack[modalStack.length - 1] === layerId)
          closeRef.current();
      }}
    >
      <section
        ref={modalRef}
        tabIndex={-1}
        className={`modal ${wide ? "modal-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <header>
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close dialog">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function FormActions({
  submitting,
  disabled,
  submitLabel = "Save",
  onCancel,
  tone = "primary",
}: {
  submitting?: boolean | undefined;
  disabled?: boolean | undefined;
  submitLabel?: string | undefined;
  onCancel?(): void;
  tone?: "primary" | "danger" | undefined;
}) {
  return (
    <div className="form-actions">
      {onCancel ? (
        <button type="button" className="button secondary" disabled={submitting} onClick={onCancel}>
          Cancel
        </button>
      ) : null}
      <button type="submit" className={`button ${tone}`} disabled={submitting || disabled}>
        {submitting ? "Working…" : submitLabel}
      </button>
    </div>
  );
}

export function ReasonDialog({
  open,
  title,
  description,
  label = "Reason",
  hint,
  initialReason = "",
  submitLabel = "Confirm",
  submitting,
  disabled,
  tone = "primary",
  minLength = 3,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  description?: string | undefined;
  label?: string | undefined;
  hint?: string | undefined;
  initialReason?: string | undefined;
  submitLabel?: string | undefined;
  submitting?: boolean | undefined;
  disabled?: boolean | undefined;
  tone?: "primary" | "danger" | undefined;
  minLength?: number | undefined;
  onClose(): void;
  onSubmit(reason: string): void;
}) {
  const [reason, setReason] = useState(initialReason);
  useEffect(() => {
    if (open) setReason(initialReason);
  }, [initialReason, open]);
  return (
    <Modal open={open} onClose={onClose} title={title} description={description}>
      <form
        className="form-stack"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(reason.trim());
        }}
      >
        <Field label={label} hint={hint}>
          <textarea
            data-autofocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            minLength={minLength}
            maxLength={2000}
          />
        </Field>
        <FormActions
          submitting={submitting}
          disabled={disabled}
          submitLabel={submitLabel}
          onCancel={onClose}
          tone={tone}
        />
      </form>
    </Modal>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  submitting,
  disabled,
  tone = "danger",
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string | undefined;
  submitting?: boolean | undefined;
  disabled?: boolean | undefined;
  tone?: "primary" | "danger" | undefined;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={title} description={description}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm();
        }}
      >
        <FormActions
          submitting={submitting}
          disabled={disabled}
          submitLabel={confirmLabel}
          onCancel={onClose}
          tone={tone}
        />
      </form>
    </Modal>
  );
}

export function formData(event: FormEvent<HTMLFormElement>): Record<string, string> {
  return Object.fromEntries(new FormData(event.currentTarget).entries()) as Record<string, string>;
}
